import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../../styles/shared/SplitFormLayout.module.css';
import T from '../T';
import LanguageSwitcher from '../LanguageSwitcher';

/**
 * SplitFormLayout - Matches the Customer Form design
 */
export default function SplitFormLayout({ 
  badge = "Internal Portal",
  title, 
  subtitle, 
  steps = [], 
  breadcrumbPath = "/staff/dashboard",
  breadcrumbLabel = "Help Center",
  middleBreadcrumb,
  currentBreadcrumb,
  children 
}) {
  const [activeSection, setActiveSection] = React.useState(steps[0]?.id);

  React.useEffect(() => {
    if (!steps.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );

    steps.forEach((step) => {
      const el = document.getElementById(step.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [steps]);

  const activeStepIndex = steps.findIndex(s => s.id === activeSection);

  return (
    <div className={styles.page}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarOrb} />
        <div className={styles.sidebarContent}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div className={styles.sidebarBadge}>
              <span className={styles.sidebarBadgeDot} />
              <span><T>{badge}</T></span>
            </div>
            <LanguageSwitcher variant="light" />
          </div>

          <h1 className={styles.sidebarTitle}>
            <T>{title}</T>
          </h1>

          <p className={styles.sidebarSub}><T>{subtitle}</T></p>

          <nav className={styles.steps}>
            {steps.map((step, index) => {
              const isActive = step.id === activeSection;
              const isCompleted = index < activeStepIndex;

              return (
                <a 
                  key={step.id} 
                  href={`#${step.id}`}
                  className={`${styles.stepItem} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div className={styles.stepNumber} />
                  <div className={styles.stepContent}>
                    <span className={styles.stepLabel}><T>{step.label}</T></span>
                    <span className={styles.stepDesc}><T>{step.desc}</T></span>
                  </div>
                </a>
              );
            })}
          </nav>

          <div className={styles.sidebarFooter}>
            <p>© 2026 Autochek Tech Support</p>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={styles.mainContent}>
        <div className={styles.formContainer}>
          <nav className={styles.breadcrumb}>
            <Link to={breadcrumbPath} className={styles.breadcrumbLink}><T>{breadcrumbLabel}</T></Link>
            
            {middleBreadcrumb && (
              <>
                <span className={styles.breadcrumbSep}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14 }}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </span>
                <Link to={middleBreadcrumb.path} className={styles.breadcrumbLink}><T>{middleBreadcrumb.label}</T></Link>
              </>
            )}

            <span className={styles.breadcrumbSep}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14 }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
            <span className={styles.breadcrumbCurrent}><T>{currentBreadcrumb}</T></span>
          </nav>

          <div className={styles.ticketContainer}>
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
